import SingleSelectComponent from "../../components/single-select/single-select.component";
import { transStatus } from "../../config/global-variables";
import IncentiveApprovalModel from "../../models/incentive-approval/incentive-approval.model";
import ViewBase from "../view-base.view";

/**
 * Incentive Approval
 *
 * FFIS payout sign-off: submit a completed run, then approve or reject it. Menu WM021104,
 * APPROVER.
 *
 * ============================================================================================
 * THIS IS THE PAGE WHERE MONEY BECOMES REAL, SO IT IS DELIBERATELY UNHURRIED
 * ============================================================================================
 * Approving does four things in one transaction: records that a named person accepted a named
 * set of figures, writes SupersededByRunId on every other run (the only place in FFIS that
 * records which run is authoritative), writes one LOCKED IncentiveFinalPayout row per employee,
 * and closes the period.
 *
 * NOTHING UNDOES IT. No route re-opens a closed period or unlocks a payout. So the confirm
 * prompts here spell out what will happen rather than asking "Are you sure?", and the page shows
 * what is being approved - employee count, total, currency, and which run - next to the button,
 * not on another screen.
 *
 * ============================================================================================
 * ⚠️ SEPARATION OF DUTIES - WHAT IS ENFORCED AND WHAT IS NOT
 * ============================================================================================
 * ENFORCED, in the stored procedure, and so unavoidable however this is called: THE PERSON WHO
 * SUBMITTED CANNOT SIGN OFF.
 *
 * NOT ENFORCED, and not fixable from here:
 *   - all three routes share ONE menu, so whoever can submit can also sign off. Separating them
 *     needs two menus, which is the role-security owner's decision;
 *   - four-eyes is not achieved. One person can submit, have a colleague sign, then sign the
 *     colleague's next submission;
 *   - if WM021103 (Calculation) is granted to the same role, whoever runs the payout also
 *     approves it.
 *
 * The page states these rather than implying the controls are tighter than they are. An approver
 * who believes the system prevents something it does not is worse off than one who knows.
 *
 * @author FFIS
 */
export default function IncentiveApprovalView() {
  ViewBase.call(this);

  const PRODUCT_GROUPS = [
    { key: 'MX', value: 'MX' },
    { key: 'AV', value: 'AV' },
  ];

  const PERIOD_TYPES = [
    { key: 'MONTHLY', value: 'MONTHLY' },
    { key: 'WEEKLY', value: 'WEEKLY' },
  ];

  const APPROVAL_STATUSES = [
    { key: '', value: '(all)' },
    { key: 'PENDING', value: 'PENDING' },
    { key: 'APPROVED', value: 'APPROVED' },
    { key: 'REJECTED', value: 'REJECTED' },
    { key: 'RECALLED', value: 'RECALLED' },
  ];

  const DEFAULT_SCOPE = {
    productGroupCode: 'MX',
    periodType: 'MONTHLY',
    periodCode: '',
  };

  let scope = structuredClone(DEFAULT_SCOPE);
  let statusFilter = '';

  let approvals = [];
  let periodStatus = [];

  /* submit form */
  let submitCalcRunId = '';
  let submitComment = '';

  /* sign-off form, per selected approval */
  let selected = null;
  let signoffComment = '';
  let closePeriod = true;

  const readEnvelope = (result) => (Array.isArray(result) ? result[0] : result?.transResult?.[0]);

  const scopeIsComplete = () => !!(scope.productGroupCode && scope.periodType && scope.periodCode);

  /* ---- loading ----------------------------------------------------------- */

  const search = () => {
    this.showLoading();
    IncentiveApprovalModel.searchApprovals({
      productGroupCode: scope.productGroupCode,
      periodType: scope.periodType,
      periodCode: scope.periodCode,
      statusCode: statusFilter || undefined,
    })
      .then((result) => {
        approvals = result?.[0] || result?.approvals || [];
        periodStatus = result?.[1] || result?.periodStatus || [];
        /* Drop a selection that is no longer in the list, or the sign-off panel would act on a
           stale row - and the action it offers is irreversible. */
        if (selected && !approvals.some(a => a.approvalId === selected.approvalId)) {
          selected = null;
        }
      })
      .catch((error) => { this.handleError(error); })
      .finally(() => { this.hideLoading(); });
  };

  /* ---- submit ------------------------------------------------------------ */

  const onSubmit = () => {
    if (!submitCalcRunId) {
      this.showMessage(
        this.getText('EM00000295', 'Enter the calculation run to submit.'),
        transStatus.failed);
      return;
    }

    this.showConfirm(
      this.getText('EM00000296',
        'Submit run ' + submitCalcRunId + ' for sign-off? This is a REQUEST - it writes nothing '
      + 'to the results and locks nothing. You will not be able to sign it off yourself.'),
      this.getText('LT00000022', 'Save'),
      (e) => {
        if (e !== 'YES') {
          return;
        }
        this.showLoading();
        IncentiveApprovalModel.submit({
          calcRunId: Number(submitCalcRunId),
          approverRole: 'APPROVER',
          comment: submitComment,
        })
          .then((result) => {
            const trans = readEnvelope(result);
            this.showMessage(
              this.getText(trans?.code, trans?.message),
              trans?.transStatus,
              () => {
                if (trans?.transStatus === transStatus.success) {
                  submitCalcRunId = '';
                  submitComment = '';
                  search();
                }
              });
          })
          .catch((error) => { this.handleError(error); })
          .finally(() => { this.hideLoading(); });
      });
  };

  /* ---- sign-off ---------------------------------------------------------- */

  const doSignoff = (decision) => {
    if (!selected) {
      return;
    }
    if (decision === 'REJECT' && !signoffComment.trim()) {
      /* The server refuses this too. Caught here so the approver is told why before a round
         trip, not instead of the server's guard. */
      this.showMessage(
        this.getText('EM00000297',
          'A rejection needs a reason. Without one the preparer has nothing to act on and the '
        + 'record cannot say why the payout was held.'),
        transStatus.failed);
      return;
    }

    /* ⚠️ rowsSucceeded, NOT employeeCount - IncentiveApprovalGet returns the run's
       RowsProcessed / RowsSucceeded / RowsFailed and has no employeeCount column. The first
       version read the missing field, so this prompt said "writes  LOCKED payout row(s)" with
       a blank where the number goes - on the one action in FFIS that cannot be undone. */
    const employees = selected.rowsSucceeded;
    const prompt = decision === 'APPROVE'
      ? this.getText('EM00000298',
          'APPROVE run ' + selected.calcRunId + '?\n\n'
        + 'This writes ' + (employees === null || employees === undefined ? '?' : employees)
        + ' LOCKED payout row(s) totalling '
        + (selected.totalAmount || '') + ' ' + (selected.currency || '') + ', marks every other '
        + 'run superseded'
        + (closePeriod ? ', and CLOSES the period so no upload or calculation can change it'
                       : ' and leaves the period OPEN, so these figures can still move')
        + '.\n\nNOTHING UNDOES THIS. There is no route to re-open a closed period or unlock a '
        + 'payout. Continue?')
      : this.getText('EM00000299',
          'REJECT run ' + selected.calcRunId + '? Nothing is locked; the run can be corrected '
        + 'and resubmitted.');

    this.showConfirm(prompt, this.getText('LT00000022', 'Save'), (e) => {
      if (e !== 'YES') {
        return;
      }
      this.showLoading();
      IncentiveApprovalModel.signoff({
        approvalId: selected.approvalId,
        decision: decision,
        comment: signoffComment,
        /* Only meaningful on APPROVE. Sent as null on REJECT rather than false, because false
           would read as a deliberate "keep the period open" decision that nobody made. */
        closePeriod: decision === 'APPROVE' ? closePeriod : null,
      })
        .then((result) => {
          const trans = readEnvelope(result);
          this.showMessage(
            this.getText(trans?.code, trans?.message),
            trans?.transStatus,
            () => {
              if (trans?.transStatus === transStatus.success) {
                selected = null;
                signoffComment = '';
                search();
              }
            });
        })
        .catch((error) => { this.handleError(error); })
        .finally(() => { this.hideLoading(); });
    });
  };

  /* ---- render helpers ---------------------------------------------------- */

  const num = (v, dp) => (v === null || v === undefined
    ? '-'
    : Number(v).toLocaleString(undefined, {
        minimumFractionDigits: dp || 0, maximumFractionDigits: dp || 0 }));

  const statusBadge = (status) => {
    const cls = status === 'APPROVED' ? 'bg-success'
      : status === 'PENDING' ? 'bg-warning text-dark'
      : status === 'REJECTED' ? 'bg-danger'
      : 'bg-secondary';
    return m('span.badge', { class: cls }, status || '-');
  };

  /* ---- sections ---------------------------------------------------------- */

  const dutiesNotice = () => m('.alert.alert-secondary.small', [
    m('div.fw-semibold', this.getText('LT00000373', 'What this system does and does not check')),
    m('ul.mb-0', [
      m('li', this.getText('EM00000300',
        'ENFORCED: the person who submitted a run cannot sign it off.')),
      m('li', this.getText('EM00000301',
        'NOT enforced: submit and sign-off share one menu, so whoever can do one can do the '
      + 'other. Separating them needs two menus - a role-security change, not a setting here.')),
      m('li', this.getText('EM00000302',
        'NOT four-eyes: one person can submit, have a colleague sign, then sign the colleague\'s '
      + 'next submission.')),
    ]),
  ]);

  const scopeSection = () => m('.card.mb-3', [
    m('.card-header.fw-semibold', this.getText('LT00000341', 'Scope')),
    m('.card-body', m('.row.g-4', [
      m('.col-md-3.position-relative', [
        m('label.form-label[for=apProductGroup]', this.getText('LT00000008', 'Product Group')),
        m(SingleSelectComponent, {
          id: 'apProductGroup',
          items: PRODUCT_GROUPS,
          selectedItem: scope.productGroupCode,
          checkmark: true,
          keyOnly: true,
          onSelect: (item) => { scope.productGroupCode = item; },
        }),
      ]),
      m('.col-md-3.position-relative', [
        m('label.form-label[for=apPeriodType]', this.getText('LT00000264', 'Period Type')),
        m(SingleSelectComponent, {
          id: 'apPeriodType',
          items: PERIOD_TYPES,
          selectedItem: scope.periodType,
          checkmark: true,
          keyOnly: true,
          onSelect: (item) => { scope.periodType = item; },
        }),
      ]),
      m('.col-md-2', [
        m('label.form-label', this.getText('LT00000265', 'Period Code')),
        m('input.form-control.form-control-bg', {
          value: scope.periodCode,
          placeholder: '202603',
          oninput: (e) => { scope.periodCode = e.target.value; },
        }),
      ]),
      m('.col-md-2.position-relative', [
        m('label.form-label[for=apStatus]', this.getText('LT00000293', 'Status')),
        m(SingleSelectComponent, {
          id: 'apStatus',
          items: APPROVAL_STATUSES,
          selectedItem: statusFilter,
          checkmark: true,
          keyOnly: true,
          onSelect: (item) => { statusFilter = item; },
        }),
      ]),
      m('.col-md-2.d-flex.align-items-end',
        m('button.btn.btn-outline-primary.w-100', {
          disabled: !scopeIsComplete(),
          onclick: search,
        }, this.getText('LT00000021', 'Search'))),
    ])),
  ]);

  const periodSection = () => {
    if (!periodStatus.length) {
      return null;
    }
    return m('.card.mb-3', [
      m('.card-header.fw-semibold', this.getText('LT00000374', 'Period status')),
      m('.card-body.p-0', m('.table-responsive',
        m('table.table.table-sm.mb-0', [
          m('thead', m('tr', [
            m('th', this.getText('LT00000008', 'Product Group')),
            m('th', this.getText('LT00000265', 'Period')),
            m('th', this.getText('LT00000375', 'Closed')),
            m('th', this.getText('LT00000376', 'Closed by')),
            m('th', this.getText('LT00000377', 'Closed on')),
          ])),
          m('tbody', periodStatus.map(row => m('tr', [
            m('td', row.productGroupCode || m('em.text-muted', '(all)')),
            m('td', row.periodType + ' ' + row.periodCode),
            m('td', row.closed
                ? m('span.badge.bg-secondary', this.getText('LT00000378', 'CLOSED'))
                : m('span.badge.bg-success', this.getText('LT00000379', 'OPEN'))),
            m('td.small.text-muted', row.closedBy || '-'),
            m('td.small.text-muted', row.closedDate || '-'),
          ]))),
        ]))),
    ]);
  };

  const submitSection = () => m('.card.mb-3', [
    m('.card-header.fw-semibold', this.getText('LT00000380', 'Submit a run for sign-off')),
    m('.card-body', [
      m('.row.g-3.align-items-end', [
        m('.col-md-3', [
          m('label.form-label', this.getText('LT00000290', 'Run')),
          m('input.form-control.form-control-bg', {
            value: submitCalcRunId,
            placeholder: this.getText('LT00000381', 'calculation run id'),
            oninput: (e) => { submitCalcRunId = e.target.value; },
          }),
        ]),
        m('.col-md-7', [
          m('label.form-label', this.getText('LT00000382', 'Comment')),
          m('input.form-control.form-control-bg', {
            value: submitComment,
            placeholder: this.getText('LT00000383',
              'What you checked before submitting'),
            oninput: (e) => { submitComment = e.target.value; },
          }),
        ]),
        m('.col-md-2',
          m('button.btn.btn-primary.w-100', {
            onclick: onSubmit,
          }, this.getText('LT00000384', 'Submit'))),
      ]),
      m('.small.text-muted.mt-2', this.getText('EM00000303',
        'Refused for a run that is not COMPLETED, a simulation, a NIGHTLY run (FR-03 exempt, so '
      + 'nobody reviewed its inputs), a run with no employee results, or one already submitted. '
      + 'Those are validation results, not errors.')),
    ]),
  ]);

  const approvalsSection = () => m('.card.mb-3', [
    m('.card-header.fw-semibold', this.getText('LT00000385', 'Approval chain')),
    m('.card-body.p-0',
      approvals.length === 0
        ? m('.p-3.text-muted', this.getText('LT00000386', 'No approvals for this scope.'))
        : m('.table-responsive', m('table.table.table-sm.table-hover.mb-0', [
            m('thead', m('tr', [
              m('th', this.getText('LT00000387', 'Approval')),
              m('th', this.getText('LT00000290', 'Run')),
              m('th', this.getText('LT00000293', 'Status')),
              m('th.text-end', this.getText('LT00000294', 'Employees')),
              m('th.text-end', this.getText('LT00000296', 'Total')),
              m('th', this.getText('LT00000388', 'Submitted by')),
              m('th', this.getText('LT00000389', 'Approved by')),
              m('th', ''),
            ])),
            m('tbody', approvals.map(row => [
              m('tr.cursor-pointer', {
                class: selected && selected.approvalId === row.approvalId ? 'table-active' : '',
                onclick: () => {
                  selected = row;
                  signoffComment = '';
                  closePeriod = true;
                },
              }, [
                m('td', row.approvalId),
                m('td', row.calcRunId),
                m('td', statusBadge(row.statusCode)),
                m('td.text-end', num(row.rowsSucceeded)),
                m('td.text-end', num(row.totalAmount, 2)),
                m('td.small.text-muted', row.submittedBy),
                m('td.small.text-muted', row.approvedBy || '-'),
                m('td.text-end',
                  row.statusCode === 'PENDING'
                    && m('span.small.text-primary', this.getText('LT00000390', 'select'))),
              ]),

              /* ⚠️ Rendered as its own row rather than a tooltip. It means the approved figures
                 are no longer the latest, which is the single thing an approver most needs to
                 notice and the easiest to hide in a hover. */
              row.staleWarning && m('tr',
                m('td.bg-warning-subtle.small[colspan=8]', [
                  m('i.bi.bi-exclamation-triangle.me-2'),
                  row.staleWarning,
                ])),
            ])),
          ]))),
  ]);

  const signoffSection = () => {
    if (!selected) {
      return null;
    }
    if (selected.statusCode !== 'PENDING') {
      return m('.card.mb-3', [
        m('.card-header.fw-semibold',
          this.getText('LT00000391', 'Approval') + ' ' + selected.approvalId),
        m('.card-body.text-muted', this.getText('EM00000304',
          'This approval is already ' + selected.statusCode + '. A decided approval stays '
        + 'decided - there is no route to re-open one.')),
      ]);
    }

    return m('.card.mb-3.border-primary', [
      m('.card-header.fw-semibold',
        this.getText('LT00000392', 'Sign off approval') + ' ' + selected.approvalId),
      m('.card-body', [
        /* What is being approved, next to the button that approves it. */
        m('.row.g-3.mb-3', [
          m('.col-md-3.small', [
            m('div.text-muted', this.getText('LT00000290', 'Run')),
            m('div.fw-semibold', selected.calcRunId)]),
          m('.col-md-3.small', [
            m('div.text-muted', this.getText('LT00000294', 'Employees')),
            m('div.fw-semibold', num(selected.rowsSucceeded))]),
          m('.col-md-3.small', [
            m('div.text-muted', this.getText('LT00000296', 'Total')),
            m('div.fw-semibold',
              num(selected.totalAmount, 2) + ' ' + (selected.currency || ''))]),
          m('.col-md-3.small', [
            m('div.text-muted', this.getText('LT00000388', 'Submitted by')),
            m('div.fw-semibold', selected.submittedBy)]),
        ]),

        selected.staleWarning && m('.alert.alert-warning.small', selected.staleWarning),

        /* ⚠️ A run with failed rows, shown next to the button that makes it permanent. The run
           can complete and still have people it could not calculate; approving it locks a payout
           that silently omits them, and nothing afterwards revisits the decision. */
        selected.rowsFailed > 0 && m('.alert.alert-danger.small', [
          m('span.fw-semibold', num(selected.rowsFailed) + ' '),
          this.getText('EM00000307',
            'row(s) in this run FAILED to calculate. They will have no payout row. Check the '
          + 'run detail on the Calculation page before approving - this cannot be revisited.'),
        ]),

        m('.small.text-muted.mb-3',
          this.getText('LT00000397', 'Scheme') + ': '
          + (selected.schemeCode || '?') + ' ' + (selected.schemeVersion || '')
          + '  |  ' + this.getText('LT00000398', 'Role') + ': '
          + (selected.approverRole || '?')),

        m('.mb-3', [
          m('label.form-label', this.getText('LT00000382', 'Comment')),
          m('input.form-control.form-control-bg', {
            value: signoffComment,
            placeholder: this.getText('LT00000393',
              'Required to reject. For an approval, what you checked.'),
            oninput: (e) => { signoffComment = e.target.value; },
          }),
        ]),

        m('.form-check.mb-3', [
          m('input.form-check-input[type=checkbox]#apClosePeriod', {
            checked: closePeriod,
            onchange: (e) => { closePeriod = e.target.checked; },
          }),
          m('label.form-check-label[for=apClosePeriod]',
            this.getText('LT00000394', 'Close the period on approval')),
          m('.form-text', this.getText('EM00000305',
            'Closing stops any further upload or calculation for the period - which is what '
          + 'makes the approved figures stable. Leave it unticked only for a subsidiary that '
          + 'approves weekly and needs the month left open, accepting that these figures can '
          + 'still move.')),
        ]),

        m('.alert.alert-danger.small', this.getText('EM00000306',
          'Approving writes one LOCKED payout row per employee, marks every other run '
        + 'superseded, and cannot be undone. No route re-opens a closed period or unlocks a '
        + 'payout.')),

        m('.d-flex.gap-2', [
          m('button.btn.btn-primary', {
            onclick: () => { doSignoff('APPROVE'); },
          }, this.getText('LT00000395', 'Approve')),
          m('button.btn.btn-outline-danger', {
            onclick: () => { doSignoff('REJECT'); },
          }, this.getText('LT00000396', 'Reject')),
          m('button.btn.btn-outline-secondary', {
            onclick: () => { selected = null; },
          }, this.getText('LT00000023', 'Cancel')),
        ]),
      ]),
    ]);
  };

  /* ---- view -------------------------------------------------------------- */

  return {
    oninit: () => {
      /* Nothing loads until a period is given. */
    },

    view: () => {
      return [
        dutiesNotice(),
        scopeSection(),
        periodSection(),
        submitSection(),
        approvalsSection(),
        signoffSection(),
        this.getBaseViews(),
      ];
    },
  };
}
